import { useState, useRef, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { LogErrors } from './../../../utilities/LogErrors'
import { useLanguage } from './../../../utilities/LanguageContext' // Importa el hook

//Funciones comunes
import { Login as BLLogin, ShowLogoutMessage } from './../../../utilities/BL/Logins'
import { ValidateSession, DeleteSessionInfo } from './../../../utilities/BL/Sessions'

//Componentes comunes
import { Input } from './../Fields/Input'
import { Checkbox } from './../Fields/Checkbox'
//import { Button } from './../../Common/Button'
import { Loading } from './../../Common/Loading'
import { Credits } from './../../Common/Credits/Credits'
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage'


const Login = () => {
  const { translate } = useLanguage() // Obtén la función de traducción desde el contexto
  const { search } = useLocation()
  const navigate = useNavigate()
  const { showMessage } = useFloatMessage()

  //Estados
  const [formData, setFormData] = useState({user: '', password: '', remember: false}) // Estado que maneja la info del formulario
  const [errors, setErrors] = useState({}) // Estado que maneja los errores
  const [isSubmitted, setIsSubmitted] = useState(false) // Estado que controla si se ha enviado, o no, el formulario
  const [isLoading, setIsLoading] = useState(true)
  const [isButtonLoading, setIsButtonLoading] = useState(false)
  
  // Objetos que almacenan los controles del formulario
  const userRef = useRef(null)
  const passwordRef = useRef(null)


  //--- Funciones personalizadas ---//
  const LoadPage = async () => {
    try {
      const session = await ValidateSession()

      if (session.success) {
        if (session.data.user.userStatus === 2) {
          showMessage({
              type: MESSAGE_TYPE.INFO,
              text: `Has llegado aquí porque tu sesión aún se encuentra activa`
          })
          navigate('/dashboard') // Navega al dashboard si el login fue exitoso
        } else {
          navigate('/confirm')
        }
      } else {
        DeleteSessionInfo()
        setIsLoading(false)
      }
    } catch (error) {
      await LogErrors('LoadPage - Login', error)
    }
  }

  const Login = async (e) => {
    try {
      e.preventDefault()
      setIsButtonLoading(true)

      const session = ValidateSession()

      if (session.success) {
        if (session.data.userStatus === 2) {
          navigate('/dashboard') // Navega al dashboard si el login fue exitoso
        } else {
          navigate('/confirm')
        }
      } else {
        setIsSubmitted(true) // Marcar el formulario como enviado

        const response = await BLLogin(formData)

        if (response.success) {
          if (response.data.user.userStatus === 2) {
            navigate('/dashboard') // Navega al dashboard si el login fue exitoso
          } else {
            navigate('/confirm')
          }
        } else {
          setErrors(response.errors) // Muestra los errores si los hay

          // Hacer focus en el campo correspondiente
          if (response.errors.user) {
            userRef.current.focus()
          } else if (response.errors.password) {
            passwordRef.current.focus()
          }
        }
      }
    } catch (error) {
      await LogErrors('Login - Login', error)
    } finally {
      setIsButtonLoading(false)
    }
  }
  //--- Funciones personalizadas ---//



  //--- Eventos de la página web ---//
  useEffect(() => {
    //Establecer el tema claro a la web
    document.body.classList.remove('DarkTheme')
    document.body.classList.add('BodyComplete')

    
    const response = ShowLogoutMessage(search)
    
    if (response.success) {
      showMessage({
        type: MESSAGE_TYPE.INFO,
        text: 'Tu sesión ha finalizado. Ingresa nuevamente'
      })

      // (Opcional) Limpia el query param para no repetir acción al navegar
      navigate({ search: "" }, { replace: true })
    } else {  
      // (Opcional) Manejo de error/param inválido
      navigate({ search: "" }, { replace: true })
    }

    
    LoadPage()
  },[search, navigate])
  //--- Eventos de la página web ---//



  //--- Eventos de formulario ---//
  /**
   * Función que se ejecuta cuando se realiza un cambio en el checkbox del formulario.
   * Obtiene la info de los campos del formulario y actualiza el valor del checkbox (True o False)
   * @param {Event} e 
   */
  const InputChange = async (e) => {
    try {  
      const { id, value, type, checked } = e.target
      
      setFormData({
        ...formData,
        [id]: type === 'checkbox' ? checked : value
      })
    } catch (error) {
      await LogErrors('InputChange - Login', error)
    }
  }

  /**
   * Función que se ejecuta cuando se presiona la tecla Enter o se presiona el botón del formulario
   * @param {Event} e 
   */
  const FormSubmit = async (e) => {
    try {
      Login(e)
    } catch (error) {
      await LogErrors('FormSubmit - Login', error)
    }
  }

  /**
   * Función que se ejecuta al presiona la tecla Enter en el formulario
   * @param {Event} e 
   */
  const EnterKeyPressInForm = async (e) => {
    try {
      if (e.key === 'Enter') {
        Login(e)
      }
    } catch (error) {
      await LogErrors('EnterKeyPressInForm - Login', error)
    }
  }
  //--- Eventos de formulario ---//


  return (
    <>
      <main className='Login Container-FullHeight Fonts--PlusJakartaSans'> {/* Container-FlexContent */}
        {isLoading ? (
          <Loading />
        ) : (
            <>
                <section className='Login__Aside'>
                  <div className='Login__Aside__Image Tables--Shadow'></div>
                </section>

                <section className='Login__Container container'>
                    <img src="./img/brand/Ztudia-Logotipo-Negro-350x135.png" className='Login__Container__Logo'/>

                    <form className="Login__Container__Form" onSubmit={FormSubmit} onKeyPress={EnterKeyPressInForm}>
                        <p id='formTitle' className='Login__Container__Form__Title'>{translate('labels.login.title')}</p>
                        
                        <Input
                            type={'text'}
                            id={'user'}
                            value={formData.user}
                            onChange={InputChange}
                            placeholder={translate('labels.login.username')}
                            error={isSubmitted ? errors.user : null} // Mostrar error solo si se ha enviado el formulario
                            isSubmitted={isSubmitted} // Pasar isSubmitted al Input
                            ref={userRef}
                        />

                        <Input
                            type={'password'} 
                            id={'password'}
                            value={formData.password}
                            onChange={InputChange}
                            placeholder={translate('labels.login.password')}
                            error={isSubmitted ? errors.password : null} // Mostrar error solo si se ha enviado el formulario
                            isSubmitted={isSubmitted} // Pasar isSubmitted al Input
                            ref={passwordRef}
                        />

                        <Checkbox id='remember' checked={formData.remember} onChange={InputChange} label={translate('labels.login.remember')} extraClassName={'Login__Container__Form__Remindme'} />

                        {isButtonLoading ? (
                            <button type='button' className='Btn__Ztudia--Primary loginForm-Button loginForm-Button-Disabled'>{translate('labels.login.loadingSubmit')}</button>
                        ) : (
                            <button type='submit' className='loginForm-Button'>{translate('labels.login.submit')}</button>
                        )}
                        {errors.general && <p className='loginForm-Error-Message'>{errors.general}</p>}
                    </form>

                    <p className='text-center mt-4'>¿Aún no tienes cuenta Ztudia?</p>
                    <button type='button' className='Btn__Ztudia--PrimaryOutline Login__NewAccount__Buttons' onClick={() => navigate('/signup')}>Crear mi cuenta aquí</button>

                    <div className='Login__Download mt-5'>
                        <a className='Login__Download__Link Btn__Ztudia--Link' onClick={() => navigate('/download')}><i className="bi bi-download"></i> Descargar solución Ztudia</a>
                    </div>

                    <Credits />
                </section>
            </>
        )}
      </main>
    </>
  )
}

export { Login };